import { Provider } from '@vgtv/api-client';
import type { ConsentManager } from '@vgtv/consent-manager';

import { getPluginUrl } from '../../config/helpers/staticUrls';
import { initChromecastProvider } from '../../providers/chromecast/chromecastProvider';
import { loadScript } from '../../utils/loadScript';
import { epoch } from '../../utils/datetime';
import { PlaylistItemManager } from '../skin-vgtv2/utils/PlaylistItemManager';

/**
 * @see {https://github.schibsted.io/svp/web-player/blob/master/src/js/player/playback/config/chromecast.js}
 */
const MEDIA_RECEIVER_APP_IDS = {
  vgtv: 'A5965089',
  ab: 'CB3CFFEE',
};

type ChromecastPluginConfig = {
  vendor: Provider;
  site?: string;
  externalUid?: string;
  consentManager?: ConsentManager;
  getUserAccesses?: () => Promise<string[]>;
};

class ChromecastPlugin {
  private readonly player: jwplayer.JWPlayer;

  private readonly config: ChromecastPluginConfig;

  private sessionStartTimestamp: number;

  constructor(player: jwplayer.JWPlayer, config: ChromecastPluginConfig) {
    this.player = player;
    this.config = config;
    this.sessionStartTimestamp = epoch();

    if (config.vendor in MEDIA_RECEIVER_APP_IDS) {
      this.setup();
    }
  }

  setup() {
    this.player.on('ready', this.loadCastApi.bind(this));
  }

  async initializeCastApi() {
    cast.framework.CastContext.getInstance().setOptions({
      receiverApplicationId: MEDIA_RECEIVER_APP_IDS[this.config.vendor],
      autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      resumeSavedSession: true,
    });

    jwplayer.api.registerProvider(
      initChromecastProvider({
        player: this.player,
        ...this.config,
      })
    );

    const context = cast.framework.CastContext.getInstance();

    context.addEventListener(
      cast.framework.CastContextEventType.CAST_STATE_CHANGED,
      this.handleCastStateChanged.bind(this)
    );

    context.addEventListener(
      cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      this.handleSessionStateChanged.bind(this)
    );
  }

  triggerCastEvent() {
    const context = cast.framework.CastContext.getInstance();

    const isAvailable =
      context.getCastState() !== cast.framework.CastState.NO_DEVICES_AVAILABLE;
    const session = context.getCurrentSession();
    const deviceName = session?.getCastDevice().friendlyName ?? '';

    this.player.trigger('cast', {
      available: isAvailable,
      active: Boolean(session),
      deviceName,
      type: 'cast',
    });
  }

  handleCastStateChanged({ castState }: cast.framework.CastStateEventData) {
    this.player.trigger('castStateChanged', {
      castState,
    });

    this.triggerCastEvent();
  }

  async handleSessionStateChanged({
    sessionState,
  }: cast.framework.SessionStateEventData) {
    const event: jwplayer.CastSessionStateChangedParam = {
      sessionState,
    };

    if (
      sessionState === cast.framework.SessionState.SESSION_STARTED ||
      sessionState === cast.framework.SessionState.SESSION_RESUMED
    ) {
      this.sessionStartTimestamp = epoch();
      await this.reloadPlaylist();
    } else if (sessionState === cast.framework.SessionState.SESSION_ENDED) {
      await this.reloadPlaylist({ autoplay: true, skipPreviousAds: true });
      event.duration = (epoch() - this.sessionStartTimestamp) * 1000;
    }

    this.player.trigger('castSessionStateChanged', event);

    this.triggerCastEvent();
  }

  async reloadPlaylist({
    autoplay = false,
    skipPreviousAds = false,
  }: {
    autoplay?: boolean;
    skipPreviousAds?: boolean;
  } = {}) {
    const currentTime = this.player.getCurrentTime();

    const manager = PlaylistItemManager.fromPlaylist(this.player);
    if (!manager) {
      return;
    }

    if (!manager.hasValidToken()) {
      await manager.refreshToken();
    }

    if (skipPreviousAds) {
      manager.removePrerollAds();
      manager.removeMidrollAdsBeforeTime(currentTime);
    }

    manager.removePauseAd();

    manager.setStartTime(currentTime);
    this.player.load(manager.getItem());
    if (autoplay) {
      this.player.play();
    }
  }

  async loadCastApi() {
    const environment = this.player.getEnvironment();

    if (!environment.Browser.chrome || !window.chrome) {
      return;
    }

    window.__onGCastApiAvailable = (isAvailable) => {
      if (isAvailable) {
        this.initializeCastApi();
      }
    };

    await loadScript(
      'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1'
    );
  }
}

(
  window.jwplayerPluginJsonp || window.jwplayer().registerPlugin
)<ChromecastPluginConfig>(getPluginUrl('chromecast'), '8.1', ChromecastPlugin);
